import React, { useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BeakerIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import {
  useLatestWorkspaceWarehouseProfilingRun,
  useReviewWorkspaceWarehouseJoinEvidence,
  useStartWorkspaceWarehouseProfilingRun,
  useWorkspaceWarehouseProfilingEvidence,
} from '../../hooks/useWorkspaceSemanticContext';
import type {
  WorkspaceWarehouseJoinEvidence,
  WorkspaceWarehouseJoinReviewAction,
  WorkspaceWarehouseProfilingRun,
} from '../../hooks/useWorkspaceSemanticContext';
import {
  Badge,
  Button,
  Progress,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Spinner,
  Textarea,
} from '../ui';

export interface WorkspaceWarehouseProfilingPanelProps {
  workspaceId: number;
  connectorId: number | null;
}

const RUNNING_STATUSES = new Set(['pending', 'running']);

const EXPANDED_JOIN_EXPLORATION_BUDGET = {
  maximum_bytes_billed: 10_000_000_000,
  maximum_total_bytes_billed: 100_000_000_000,
  timeout_seconds: 300,
  maximum_join_candidates: 5_000,
  maximum_run_seconds: 3_600,
};

function numericValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function runProgress(run: WorkspaceWarehouseProfilingRun): number {
  const explicit =
    numericValue(run.progress.percentage) ??
    numericValue(run.progress.percent) ??
    numericValue(run.progress.progress_percentage);
  if (explicit != null) return Math.min(100, Math.max(0, explicit));
  const completedQueries = numericValue(run.progress.query_completed);
  const totalQueries = numericValue(run.progress.query_total);
  if (completedQueries != null && totalQueries && totalQueries > 0) {
    return Math.min(100, Math.round((completedQueries / totalQueries) * 100));
  }
  const completed =
    numericValue(run.progress.completed_tables) ??
    numericValue(run.progress.processed_tables);
  const total = numericValue(run.progress.total_tables);
  return completed != null && total && total > 0
    ? Math.min(100, Math.round((completed / total) * 100))
    : RUNNING_STATUSES.has(run.status)
      ? 5
      : run.status.startsWith('completed')
        ? 100
        : 0;
}

function formatBytes(value: number | null | undefined): string {
  if (value == null) return 'Not recorded';
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KB`;
  return `${(value / 1_048_576).toFixed(1)} MB`;
}

function formatRate(value: number | null | undefined): string {
  return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
}

function statusBadge(status: string) {
  if (status === 'completed') return { variant: 'success' as const, label: 'Complete' };
  if (status === 'completed_with_omissions') return { variant: 'warning' as const, label: 'Complete with omissions' };
  if (status === 'failed') return { variant: 'danger' as const, label: 'Failed' };
  if (RUNNING_STATUSES.has(status)) return { variant: 'info' as const, label: status === 'pending' ? 'Queued' : 'Running' };
  return { variant: 'secondary' as const, label: status.replace(/_/g, ' ') };
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
        {label}
      </dt>
      <dd className="mt-1 break-words font-mono text-sm text-charcoal dark:text-gray-100">{value}</dd>
    </div>
  );
}

function JoinEvidenceCard({
  evidence,
  isReviewing,
  onReview,
}: {
  evidence: WorkspaceWarehouseJoinEvidence;
  isReviewing: boolean;
  onReview: (
    evidenceId: number,
    action: WorkspaceWarehouseJoinReviewAction,
    reviewerNotes?: string,
  ) => Promise<unknown>;
}) {
  const [notes, setNotes] = useState(evidence.reviewer_notes ?? '');
  const source = evidence.source_column_names.join(' + ') || `table ${evidence.source_table_id}`;
  const target = evidence.target_column_names.join(' + ') || `table ${evidence.target_table_id}`;
  const canApprove =
    evidence.validation_state === 'physically_validated' && !evidence.unsafe_fanout;

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold text-charcoal dark:text-gray-100">
            {source} → {target}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Tables {evidence.source_table_id} → {evidence.target_table_id}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant={evidence.review_state === 'approved' ? 'success' : 'secondary'}>
            {evidence.review_state.replace(/_/g, ' ')}
          </Badge>
          <Badge variant={evidence.is_executable ? 'success' : 'warning'}>
            {evidence.is_executable ? 'Executable' : 'Non-executable'}
          </Badge>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Metric label="Match" value={formatRate(evidence.match_rate)} />
        <Metric
          label="Null source / target"
          value={`${formatRate(evidence.source_null_rate)} / ${formatRate(evidence.target_null_rate)}`}
        />
        <Metric
          label="Unique source / target"
          value={`${formatRate(evidence.source_uniqueness_rate)} / ${formatRate(evidence.target_uniqueness_rate)}`}
        />
        <Metric label="Cardinality" value={evidence.cardinality?.replace(/_/g, ' ') ?? '—'} />
        <Metric label="Fanout" value={evidence.fanout_ratio?.toFixed(2) ?? '—'} />
        <Metric label="Bytes" value={formatBytes(evidence.query_bytes_processed)} />
      </dl>

      {evidence.unsafe_fanout && (
        <div className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200" role="alert">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
          Unsafe fanout can never be approved as executable. A documented steward exception remains non-executable.
        </div>
      )}

      <div className="mt-4">
        <label htmlFor={`join-review-notes-${evidence.id}`} className="text-xs font-semibold text-charcoal dark:text-gray-100">
          Steward review note
        </label>
        <Textarea
          id={`join-review-notes-${evidence.id}`}
          className="mt-2 min-h-[72px]"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Required for a steward exception; optional for approve or reject."
          maxLength={2_000}
        />
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          type="button"
          size="sm"
          disabled={!canApprove || isReviewing}
          title={!canApprove ? 'Approval requires physically validated evidence without unsafe fanout.' : undefined}
          onClick={() => void onReview(evidence.id, 'approve', notes)}
        >
          <CheckCircleIcon className="h-4 w-4" />
          Approve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={isReviewing}
          onClick={() => void onReview(evidence.id, 'reject', notes)}
        >
          Reject
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!notes.trim() || isReviewing}
          onClick={() => void onReview(evidence.id, 'steward_exception', notes)}
        >
          <ScaleIcon className="h-4 w-4" />
          Steward exception
        </Button>
      </div>
    </article>
  );
}

export function WorkspaceWarehouseProfilingPanel({
  workspaceId,
  connectorId,
}: WorkspaceWarehouseProfilingPanelProps) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const latest = useLatestWorkspaceWarehouseProfilingRun(workspaceId, connectorId);
  const startRun = useStartWorkspaceWarehouseProfilingRun(workspaceId);
  const run = latest.data ?? null;
  const evidence = useWorkspaceWarehouseProfilingEvidence(
    workspaceId,
    run?.id ?? null,
    evidenceOpen,
  );
  const review = useReviewWorkspaceWarehouseJoinEvidence(workspaceId, run?.id ?? null);
  const status = run ? statusBadge(run.status) : null;
  const bytesProcessed =
    numericValue(run?.progress.bytes_processed) ??
    numericValue(run?.diagnostics?.bytes_processed);
  const phase = typeof run?.progress.phase === 'string' ? run.progress.phase.replace(/_/g, ' ') : null;
  const joinCount = evidence.data?.joins.length;
  const budgetSummary = useMemo(() => {
    if (!run) return null;
    return {
      perQuery: numericValue(run.budget.maximum_bytes_billed),
      total: numericValue(run.budget.maximum_total_bytes_billed),
      rows: numericValue(run.budget.maximum_rows),
      seconds: numericValue(run.budget.maximum_run_seconds),
    };
  }, [run]);

  const reviewEvidence = (
    evidenceId: number,
    action: WorkspaceWarehouseJoinReviewAction,
    reviewerNotes?: string,
  ) => review.mutateAsync({ evidenceId, action, reviewerNotes });

  return (
    <section aria-labelledby="warehouse-profiling-title" className="border-b border-gray-200 bg-gray-50/70 px-5 py-5 dark:border-dark-border dark:bg-dark-surface-2/60">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <BeakerIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 id="warehouse-profiling-title" className="font-subtitle text-lg text-charcoal dark:text-gray-100">
              Bounded BigQuery profiling
            </h3>
            {status && <Badge variant={status.variant}>{status.label}</Badge>}
          </div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            Persist table, column, and join evidence under explicit row, time, and byte budgets before ontology generation.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            size="sm"
            disabled={!connectorId || startRun.isPending || (run ? RUNNING_STATUSES.has(run.status) : false)}
            onClick={() => connectorId && void startRun.mutateAsync({ connectorId })}
          >
            {(startRun.isPending || (run && RUNNING_STATUSES.has(run.status))) && <Spinner size="sm" />}
            {run && RUNNING_STATUSES.has(run.status) ? 'Profiling in progress' : 'Run bounded profiling'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!connectorId || startRun.isPending || (run ? RUNNING_STATUSES.has(run.status) : false)}
            onClick={() =>
              connectorId &&
              void startRun.mutateAsync({
                connectorId,
                budget: EXPANDED_JOIN_EXPLORATION_BUDGET,
              })
            }
          >
            Run expanded join exploration
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!run}
            onClick={() => setEvidenceOpen(true)}
          >
            Review join evidence
            {typeof joinCount === 'number' && <Badge variant="secondary">{joinCount}</Badge>}
          </Button>
        </div>
      </div>

      <p className="mt-3 max-w-4xl text-xs leading-5 text-gray-600 dark:text-gray-300">
        Expanded join exploration remains bounded: up to 10 GB per query, 100 GB per run,
        5,000 join candidates, and one hour. It can increase BigQuery charges; confirm the
        warehouse billing impact before starting it.
      </p>

      {!connectorId && (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
          Select a BigQuery connector in workspace scope before profiling.
        </p>
      )}
      {latest.isLoading && connectorId && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" role="status">
          <Spinner size="sm" />
          Loading persisted profiling status...
        </div>
      )}
      {latest.isError && (
        <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200" role="alert">
          Persisted profiling status is unavailable. Retry after checking the workspace connection.
        </p>
      )}
      {!latest.isLoading && !latest.isError && connectorId && !run && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No profiling run exists for this connector yet.
        </p>
      )}

      {run && (
        <div className="mt-4 space-y-3" aria-live="polite">
          <Progress
            value={runProgress(run)}
            showLabel
            variant={run.status === 'failed' ? 'warning' : run.status.startsWith('completed') ? 'success' : 'info'}
            indeterminate={run.status === 'pending'}
          />
          <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Metric label="Phase" value={phase ?? run.status.replace(/_/g, ' ')} />
            <Metric label="Processed" value={formatBytes(bytesProcessed)} />
            <Metric label="Per-query cap" value={formatBytes(budgetSummary?.perQuery)} />
            <Metric label="Run cap" value={formatBytes(budgetSummary?.total)} />
            <Metric label="Row cap" value={budgetSummary?.rows?.toLocaleString() ?? '—'} />
            <Metric label="Time cap" value={budgetSummary?.seconds ? `${budgetSummary.seconds}s` : '—'} />
            <Metric label="Omissions" value={run.omissions.length} />
            <Metric label="Run ID" value={`#${run.id}`} />
          </dl>
          {run.error_message && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200" role="alert">
              {run.error_message}
            </p>
          )}
        </div>
      )}

      {startRun.isError && (
        <p className="mt-3 text-sm text-rose-700 dark:text-rose-300" role="alert">
          The bounded profiling run could not be started. Another run may already own this workspace scope.
        </p>
      )}

      <Sheet open={evidenceOpen} onClose={() => setEvidenceOpen(false)}>
        <SheetContent side="right" size="2xl" data-testid="join-evidence-sheet">
          <SheetHeader>
            <SheetTitle>Physical join evidence</SheetTitle>
            <SheetDescription>
              Review persisted overlap, nullability, uniqueness, cardinality, fanout, and query-cost evidence.
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            {evidence.isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" role="status">
                <Spinner size="sm" />
                Loading join evidence...
              </div>
            )}
            {evidence.isError && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200" role="alert">
                Join evidence could not be loaded. Close the panel and retry.
              </p>
            )}
            {!evidence.isLoading && !evidence.isError && evidence.data?.joins.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-dark-border">
                <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                <p className="mt-2 font-semibold text-charcoal dark:text-gray-100">No join evidence yet</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Complete bounded profiling after ontology relationship candidates exist.
                </p>
              </div>
            )}
            {evidence.data?.joins.map((join) => (
              <JoinEvidenceCard
                key={join.id}
                evidence={join}
                isReviewing={review.isPending}
                onReview={reviewEvidence}
              />
            ))}
            {review.isError && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200" role="alert">
                The review decision was not saved. Check the evidence state and retry.
              </p>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </section>
  );
}
